import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppInformationService } from '../app-information.service';
import { environment } from 'environments/environment';
import { Guid } from './entities';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';

type ResizeMode = 'crop' | 'stretch' | 'max';

@Injectable()
export class UtilityService {
    static serviceName: string;

    constructor(private http: HttpClient, private appInfo: AppInformationService) {
        UtilityService.serviceName = environment.serviceName;
    }

    static utcToLocal(datetime: Date, roundToDay = false) {
        if (!datetime) return datetime;

        let newValue = moment(datetime).subtract(moment(datetime).utcOffset(), 'minutes');
        if (roundToDay) newValue = newValue.startOf('day');
        return newValue.toDate();
    }

    static localToUtc(datetime: Date) {
        if (!datetime) return datetime;
        else {
            return moment(datetime).add(moment(datetime).utcOffset(), 'minutes').toDate();
        }
    }

    async getServiceVersion() {
        const result = await this.http.post(`${environment.serviceName}/api/utility/getServiceVersion`, {}).toPromise();
        return result;
    }

    async logUser() {
        const version = this.appInfo.clientVersion;
        const result = await this.http.post(`${environment.serviceName}/api/utility/logUser/${version}`, null, { responseType: 'text' }).toPromise();
        return result;
    }

    async sendMessage(messageId: Guid, privateEmail = false) {
        // await this.http.post(`${environment.serviceName}/api/utility/sendMessage/${messageId}/${privateEmail}`, null, { responseType: 'text' }).toPromise();

        const result = await firstValueFrom<any>(this.http.post(`${UtilityService.serviceName}/utility/sendMessage`, { id: messageId, privateEmail }));
        return result;
    }

        async sendMessageViaSendGrid(messageId: Guid, privateEmail = false) {
        // await this.http.post(`${environment.serviceName}/api/utility/sendMessage/${messageId}/${privateEmail}`, null, { responseType: 'text' }).toPromise();

        const result = await firstValueFrom<any>(this.http.post(`${UtilityService.serviceName}/utility/SendGridMessage`, { id: messageId, privateEmail }));
        return result;
    }

    async closeAllNominations() {
        const results = await this.http.post(`${environment.serviceName}/api/utility/closeAllNominations`, null).toPromise();
        return results;
    }

    getPhoto(fileName: string, height: number = 300, width: number = 0, mode: ResizeMode = 'max') {
        const container = this.appInfo.settings.church;
        const url = this.resize(`${UtilityService.serviceName}/${container}/MemberPhotos/${fileName}`, width, height, mode);
        return url;
    }

    resize(url: string, width?: number, height?: number, mode?: ResizeMode) {
        const params = [];
        if (width) params.push(`width=${width}`);
        if (height) params.push(`height=${height}`);
        if (mode) params.push(`rmode=${mode}`);
        if (params.length > 0) url = `${url}?${params.join('&')}`;
        return url;
    }

    async rotateImage(id: number, degrees: number) {
        return this.http.post(`${UtilityService.serviceName}/file/rotate?id=${id}&degrees=${degrees}`, {}).toPromise();
    }

    // async fixFamilyNames(partId: number) {
    //     const result: any = await this.http.post(`${UtilityService.serviceName}/utility/fixNames`, { partId: partId }).toPromise();
    //     return result;
    // }

    async printPictureDirectory() {
        const url = `${UtilityService.serviceName}/reports/pictureDirectory`;
        window.open(url, '_blank');
    }

    async printFamilyStatement(familyId: number, year: number, rmd: boolean) {
        // const result: any = await this.http.post(`${UtilityService.serviceName}/reports/printStatement/${familyId}`, null).toPromise();
        // return result;

        // const url = `${UtilityService.serviceName}/givingStatement?familyId=${familyId}`;
        const url = `${UtilityService.serviceName}/reports/givingStatement?id=${familyId}&year=${year}&rmd=${rmd}`;
        window.open(url, '_blank');
    }

    async printYearlyStatements(year?: number, rmd: boolean = false) {
        if (!year) year = new Date().getFullYear();
        const url = `${UtilityService.serviceName}/reports/yearEndStatementsByYear?year=${year}&year=${year}&rmd=${rmd}`;
        window.open(url, '_blank');
    }

    async printPledgeStatements(year?: number, rmd: boolean = false) {
        const familyId = 645;
        const url = `${UtilityService.serviceName}/reports/pledgeStatement?id=${familyId}&year=${year}&rmd=${rmd}`;
        window.open(url, '_blank');
    }

    async printMemberInfoSheet(memberCode: string) {
        const url = `${UtilityService.serviceName}/reports/memberInformationSheet?memberCode=${memberCode}`;
        window.open(url, '_blank');
    }

    async printMemberInfoSheets() {
        const url = `${UtilityService.serviceName}/reports/memberInformationSheets`;
        window.open(url, '_blank');
    }

    async printMemberCalendar(filter: any) {
        const start = moment(filter.startDate).format('MM/DD/YYYY');
        const end = moment(filter.endDate).format('MM/DD/YYYY');

        const url = `${UtilityService.serviceName}/reports/memberCalendar?startDate=${start}&endDate=${end}`;
        window.open(url, '_blank');
    }

    async getMemberStatementPageInfo(memberCode: string) {
        const result = await this.http.post(`${environment.serviceName}/portal/getMemberStatementPageInfo`, { memberCode }).toPromise();
        return result;
    }

    async getMemberStatement(filter?: any) {
        const birth = moment(filter.birthDate).format('MM/DD/YYYY');
        // const result = await this.http
        //     .post(`${environment.serviceName}/api/utility/getMemberStatement`, {year: filter.givingYear, email: `${filter.email}`})
        //     .toPromise();
        const email = `${filter.email}`;
        const result = await this.http
            .post(`${environment.serviceName}/portal/getMemberStatement`, {
                year: filter.givingYear,
                email: filter.email,
                birthDate: birth,
                budgetBox: filter.budgetBox,
                memberCode: filter.code,
            })
            .toPromise();
        return result;
    }

    async getNominationLetter(personId: number, dueDate: Date, role: string) {
        const due = moment(dueDate).format('MM/DD/YYYY');
        const url = `${UtilityService.serviceName}/reports/nominationLetter?personId=${personId}&dueDate=${due}&role=${role}`;
        // const result = await this.http.post(`${UtilityService.serviceName}/reports/nominationLetter`, {personId: personId, dueDate: due, role: role}).toPromise()
        window.open(url, '_blank');
    }

    async printNomination(dueDate: Date) {
        const due = moment(dueDate).format('MM/DD/YYYY');
        const url = `${UtilityService.serviceName}/reports/nominationLetter?dueDate=${due}`;
        window.open(url, '_blank');
    }

    async getMemberCode(filter?: any) {
        const birth = moment(filter.birthDate).format('MM/DD/YYYY');
        console.log('utility birth', birth);
        const result = await this.http.post(`${environment.serviceName}/portal/getMemberCode`, { email: filter.email, birthDate: birth, budgetBox: filter.budgetBox }).toPromise();
        return result;
    }

    async getPeopleInfos() {
        const result = await this.http.post(`${environment.serviceName}/portal/peopleInfos`, {}).toPromise();
        return result;
    }

    async getCalendarInfos(startDate: Date, endDate: Date) {
        const start = moment(startDate).format('MM/DD/YYYY');
        const end = moment(endDate).format('MM/DD/YYYY');
        const result = await this.http.post(`${environment.serviceName}/portal/calendarInfos`, { startDate: start, endDate: end }).toPromise();
        return result;
    }

    async getFamilyInfos(familyId: number) {
        const result = await this.http.post(`${environment.serviceName}/portal/familyInfos`, { familyId }).toPromise();
        return result;
    }

    async getFamilyInfosByMemberCode(memberCode: string) {
        const result = await this.http.post(`${environment.serviceName}/portal/familyInfosByMemberCode`, { memberCode }).toPromise();
        return result;
    }

    async getStates() {
        const result = await this.http.post(`${environment.serviceName}/portal/getStates`, {}).toPromise();
        return result;
    }

    async updateMemberPassword() {
        const result = await this.http.post(`${environment.serviceName}/portal/updateMemberPassword`, {}).toPromise();
        return result;
    }

    async emailProposal(personIds: number[], proposalId: number, userName: string, sendAsText: boolean) {
        const result = await this.http
            .post(`${environment.serviceName}/portal/emailProposal`, { personIds: personIds, proposalId: proposalId, userName: userName, sendAsText: sendAsText })
            .toPromise();
        return result[0];
    }

    async emailProposalReport(personIds: number[], proposalId: number, userName: string) {
        const result = await this.http
            .post(`${environment.serviceName}/portal/emailProposalReport`, { personIds: personIds, proposalId: proposalId, userName: userName })
            .toPromise();
        return result[0];
    }

    async generateCongregationalProposalVotes(personIds: number[], proposalId: number, userName: string) {
        const result = await this.http
            .post(`${environment.serviceName}/portal/generateCongregationalProposalVotes`, { personIds: personIds, proposalId: proposalId, userName: userName })
            .toPromise();
        return result[0];
    }

    async sendCongregationalProposalVotes(proposalId: number, userName: string) {
        const result = await this.http.post(`${environment.serviceName}/portal/emailCongregationalProposalVotes`, { proposalId: proposalId, userName: userName }).toPromise();
        return result;
    }

    async getUnSentCongregationalProposals(proposalId: number) {
        const result = await this.http.post(`${environment.serviceName}/portal/getUnSentCongregationalVoteCount`, { proposalId: proposalId }).toPromise();
        return result;
    }

    async printProposal(proposalId: number) {
        const url = `${UtilityService.serviceName}/reports/ProposalReport?proposalId=${proposalId}`;
        window.open(url, '_blank');
    }

    async getProposal(userCode: string) {
        const result = await this.http.post(`${environment.serviceName}/portal/getProposal`, { voteCode: userCode }).toPromise();
        return result[0];
    }

    async submitProposalVote(userCode: string, yesVote: boolean, noVote: boolean) {
        const result = await this.http.post(`${environment.serviceName}/portal/submitProposalVote`, { voteCode: userCode, yesVote: yesVote, noVote: noVote }).toPromise();
        return result;
    }
    async submitProposalDiscussion(userCode: string, discussion: string) {
        const result = await this.http.post(`${environment.serviceName}/portal/submitProposalDiscussion`, { voteCode: userCode, discussion: discussion }).toPromise();
        return result;
    }

    async monthEndReport(startDate: Date, endDate: Date, statusId: number) {
        const start = moment(startDate).format('MM/DD/YYYY');
        const end = moment(endDate).format('MM/DD/YYYY');
        const url = `${UtilityService.serviceName}/reports/monthEndReport?startDate=${start}&endDate=${end}&statusId=${statusId}`;
        window.open(url, '_blank');
    }

    async bankDepositSlips(ids: number[]) {
        let queryIds = '';
        ids.map((id) => {
            queryIds = `${queryIds}&ids=${id}`;
        });
        const url = `${UtilityService.serviceName}/reports/bankDepositSlips?${queryIds}`;
        window.open(url, '_blank');
    }

    async printHouseholdsReport() {
        const url = `${UtilityService.serviceName}/reports/householdsReport`;
        window.open(url, '_blank');
    }

    async printDirectory(mode?: string) {
        let url = '';
        if (mode === 'picture') url = `${UtilityService.serviceName}/reports/picturedirectory`;
        else url = `${UtilityService.serviceName}/reports/directory`;

        window.open(url, '_blank');
    }

    async printContactInfoSheet() {
        const url = `${UtilityService.serviceName}/reports/contactInformationSheet`;
        window.open(url, '_blank');
    }
    async printPrayerSheet() {
        const url = `${UtilityService.serviceName}/reports/councilPrayerSheet`;
        window.open(url, '_blank');
    }
    async printExitGreeterSheet() {
        const url = `${UtilityService.serviceName}/reports/exitGreeterSheet`;
        window.open(url, '_blank');
    }

    async printCommitteeReport() {
        const url = `${UtilityService.serviceName}/reports/committeeReport`;
        window.open(url, '_blank');
    }

    async printCommitteeAssignment(date: Date) {
        const dueDate = moment(date).format('MM/DD/YYYY');
        const url = `${UtilityService.serviceName}/reports/committeeAssignmentReport?dueDate=${dueDate}`;
        window.open(url, '_blank');
    }

    async printNominationBallot() {
        const url = `${UtilityService.serviceName}/reports/nominationBallotReport`;
        window.open(url, '_blank');
    }

    async printNominationForm(date: Date) {
        const dueDate = moment(date).format('MM/DD/YYYY');

        const url = `${UtilityService.serviceName}/reports/nominationFormReport?dueDate=${dueDate}`;
        window.open(url, '_blank');
    }

    async printInvoice(invoiceId: number) {
        const url = `${UtilityService.serviceName}/reports/invoiceReport?invoiceId=${invoiceId}`;
        window.open(url, '_blank');
    }

    async getAnnouncementSheet(fileName: string) {
        const header = 'https://peccerium.blob.core.windows.net/graafschap/MessageAttachments/';
        const url = `${header}${fileName}`;
        window.open(url, '_blank');
    }
    async getMemberList(userCode: string) {
        const result = await this.http.post(`${environment.serviceName}/portal/nominationList`, { userCode }).toPromise();
        return result;
    }
    async submitNominations(userCode: string, deacons: any[], sheps: any[], execs: any[]) {
        const filter = {
            userCode: userCode,
            deacons: deacons,
            sheps: sheps,
            execs: execs,
        };

        const result = await this.http.post(`${environment.serviceName}/portal/submitNomination`, filter).toPromise();
        return result;
    }
    async getNominationInfos() {
        const result = await this.http.post(`${environment.serviceName}/portal/nominationInfos`, {}).toPromise();
        return result;
    }
    async getNominationExceptions() {
        const result = await this.http.post(`${environment.serviceName}/portal/getNominationExceptions`, {}).toPromise();
        return result;
    }
        async createNominationExceptionLog(message: string) {
        const result = await this.http.post(`${environment.serviceName}/portal/createNominationExceptionLog`, { message }).toPromise();
        return result;
    }
    async createNominationVotes(endDate: Date) {
        const end = moment(endDate).format('MM/DD/YYYY');
        const result = await this.http.post(`${environment.serviceName}/portal/createNominationVotes`, { endDateString: end }).toPromise();
        return result;
    }
    async emailNominations() {
        const result = await this.http.post(`${environment.serviceName}/portal/emailNominations`, {}).toPromise();
        return result;
    }

    async emailMinutes(emailId: number, memberIds: number[], sendAgendaText?: boolean) {
        const result = await this.http
            .post(`${environment.serviceName}/portal/emailMinutes`, { emailId: emailId, memberIds: memberIds, sendAgendaText: sendAgendaText })
            .toPromise();
        return result;
    }

    async emailInvoice(emailId: string, invoiceId: number) {
        const result = await firstValueFrom<any>(this.http.post(`${UtilityService.serviceName}/portal/emailInvoice`, { emailId, invoiceId }));
        return result;
    }

    async getMembersCommitteeMembers() {
        const result = await this.http.post(`${environment.serviceName}/api/utility/getMembersCommitteeMembers`, {}).toPromise();
        return result;
    }
    async getNextNumber(name: string) {
        const result = await firstValueFrom<any>(this.http.post(`${UtilityService.serviceName}/utility/getNextNumber`, { name: name }));
        return result;

        // const result = await this.http.post(`${environment.serviceName}/api/utility/getNextNumber?name=${name}`, null).toPromise();
        // return result;
    }

    async getUserCommittees(memberCode: string) {
        const result = await this.http.post(`${environment.serviceName}/portal/getUserCommittees`, { memberCode }).toPromise();
        return result;
    }

    async getCommitteeAgendas(memberCode: string, committeeId: number) {
        const result = await this.http.post(`${environment.serviceName}/portal/getCommitteeAgendas`, { memberCode, committeeId }).toPromise();
        return result;
    }
    async getCommitteeDocuments(memberCode: string, committeeId: number) {
        const result = await this.http.post(`${environment.serviceName}/portal/getCommitteeDocuments`, { memberCode, committeeId }).toPromise();
        return result;
    }

    async printAgenda(agendaId: number) {
        const url = `${UtilityService.serviceName}/reports/AgendaReport?agendaId=${agendaId}`;
        window.open(url, '_blank');
    }

    async emailAgenda(agendaId: number, emailId: number, memberIds: number[]) {
        const result = await firstValueFrom<any>(this.http.post(`${environment.serviceName}/portal/emailAgenda`, { agendaId: agendaId, emailId: emailId, memberIds: memberIds }));
        return result as boolean;
    }
    async logRecord(userName: string, message: string) {
        const result = await firstValueFrom<any>(this.http.post(`${environment.serviceName}/portal/logCommitteePageViewed`, { userName: userName, message: message }));
        return result as boolean;
    }
    async emailPhotoUploaded(userName: string, message: string) {
        const result = await firstValueFrom<any>(this.http.post(`${environment.serviceName}/portal/emailOffice`, { userName: userName, message: message }));
        return result as boolean;
    }

    async getReviewLinks(userCode: string) {
        const result = await this.http.post(`${environment.serviceName}/portal/getReviewLinks`, { memberCode: userCode }).toPromise();
        return result;
    }

    async viewLinkedDocument(reviewCode: string, reviewId: number) {
        const result = await this.http.post(`${environment.serviceName}/portal/viewLinkedDocument`, { reviewCode: reviewCode, reviewId: reviewId }).toPromise();
        return result;
    }

    async emailAgendaDocumentPackage(agendaId: number, emailId: number, memberIds: number[]) {
        const result = await firstValueFrom<any>(
            this.http.post(`${environment.serviceName}/portal/emailAgendaDocumentPackage`, { agendaId: agendaId, emailId: emailId, memberIds: memberIds })
        );
        return result as boolean;
    }
    async emailDocTest(emailId: number, committeeId: number, agendaId: number) {
        const result = await firstValueFrom<any>(
            this.http.post(`${environment.serviceName}/portal/EmailAddDocTest`, { emailId: emailId, committeeId: committeeId, agendaId: agendaId })
        );
        return result as boolean;
    }
}
// ility/getCSVDataBank?bankPath=${bankPath}&assignedMonth=${assignedMonth}
